<div class="flex flex-col gap-6">
  <div class="flex flex-col gap-3">
    <h1 class="use-grotesque m-0 text-4xl font-bold leading-none tracking-tight text-balance sm:text-5xl">
      Which brands do you <span class="text-primary">represent?</span>
    </h1>
    <p class="m-0 text-xl font-semibold leading-snug text-muted-foreground">Add each brand's website and we'll pull in its logo and details. You can add more later.</p>
  </div>

  @if (manualOpen()) {
    <form class="flex flex-col gap-4" (submit)="submitManual($event)">
      <div class="flex flex-col gap-2">
        <label for="vfa-manual-brand-name" class="text-base font-bold">Brand name</label>
        <input
          id="vfa-manual-brand-name"
          type="text"
          autocomplete="organization"
          placeholder="Brand name"
          class="h-14 rounded-lg border border-border bg-card px-4 text-lg text-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          [class.border-red-600]="onboarding.error()"
          [value]="manualName()"
          (input)="onManualName($event)" />
      </div>
      <div class="flex flex-col gap-2">
        <label for="vfa-manual-brand-website" class="text-base font-bold">Website (optional)</label>
        <input
          id="vfa-manual-brand-website"
          type="text"
          inputmode="url"
          autocomplete="url"
          autocapitalize="none"
          spellcheck="false"
          placeholder="brand.com"
          class="h-14 rounded-lg border border-border bg-card px-4 text-lg text-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          [value]="manualWebsite()"
          (input)="onManualWebsite($event)" />
      </div>
      @if (onboarding.error()) {
        <p class="m-0 text-base font-semibold text-red-600 dark:text-red-400" role="alert">{{ onboarding.error() }}</p>
      }
      <div class="flex gap-2">
        <button type="submit" class="btn-shout btn-shout-md btn-shout-primary shrink-0">Add brand</button>
        <button type="button" class="btn-shout btn-shout-md btn-shout-secondary shrink-0" (click)="closeManual()">Cancel</button>
      </div>
    </form>
  } @else {
    <form class="flex flex-col gap-2" (submit)="add($event)">
      <label for="vfa-brand-website" class="text-base font-bold">Brand website</label>
      <div class="flex gap-2">
        <div class="flex h-14 min-w-0 flex-1 overflow-hidden rounded-lg border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20" [class.border-red-600]="onboarding.error()">
          <p class="m-0 flex items-center border-r border-border bg-muted px-4 text-lg font-semibold text-muted-foreground">https://</p>
          <input
            id="vfa-brand-website"
            type="text"
            inputmode="url"
            autocomplete="url"
            autocapitalize="none"
            spellcheck="false"
            placeholder="brand.com"
            class="min-w-0 flex-1 bg-card px-4 text-lg text-foreground outline-none placeholder:text-muted-foreground disabled:bg-muted"
            [disabled]="onboarding.atBrandCap()"
            [value]="onboarding.draft().brandUrl"
            (input)="onBrandUrl($event)" />
        </div>
        <button type="submit" class="btn-shout btn-shout-md btn-shout-secondary shrink-0" [disabled]="onboarding.atBrandCap()">Add</button>
      </div>
      @if (onboarding.atBrandCap()) {
        <p class="m-0 text-base font-semibold text-muted-foreground">Your plan includes 3 brands. You can add more from the dashboard.</p>
      }
      @if (onboarding.error()) {
        <p class="m-0 text-base font-semibold text-red-600 dark:text-red-400" role="alert">{{ onboarding.error() }}</p>
      }
      @if (suggestions(); as brands) {
        @if (brands.length > 0) {
          <ul class="m-0 flex list-none flex-col gap-1 p-0">
            @for (brand of brands; track brand.brandId) {
              <li>
                <button
                  type="button"
                  class="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2 text-left hover:border-primary/40 hover:bg-primary/5"
                  (click)="select(brand)">
                  @if (brand.icon) {
                    <img [src]="brand.icon" alt="" class="size-8 shrink-0 rounded-md bg-muted object-cover" />
                  } @else {
                    <span class="size-8 shrink-0 rounded-md bg-muted" aria-hidden="true"></span>
                  }
                  <div class="min-w-0 flex-1">
                    <p class="m-0 truncate text-sm font-bold">{{ brand.name }}</p>
                    <p class="m-0 truncate text-sm text-muted-foreground">{{ brand.domain }}</p>
                  </div>
                </button>
              </li>
            }
          </ul>
        } @else {
          <p class="m-0 text-base font-semibold text-muted-foreground">Can't find your brand?</p>
        }
      }
      <button type="button" class="self-start text-lg font-bold text-primary hover:text-primary/80 disabled:text-muted-foreground" [disabled]="onboarding.atBrandCap()" (click)="openManual()">
        No website? Enter details manually
      </button>
    </form>
  }

  @if (onboarding.draft().brands.length === 0) {
    <p class="m-0 rounded-xl border border-dashed border-border p-5 text-base font-semibold text-muted-foreground">
      Your brands will appear here. Start with the one you'll brief first.
    </p>
  } @else {
    <div class="flex flex-col gap-2">
      <p class="m-0 text-base font-semibold text-muted-foreground">{{ onboarding.draft().brands.length }} added · {{ cap }} included in your plan</p>
      <ul class="m-0 list-none overflow-hidden rounded-xl border border-border p-0">
        @for (brand of onboarding.draft().brands; track brand.id; let last = $last) {
          <li class="flex items-center gap-3 px-3 py-3" [class]="last ? '' : 'border-b border-border'">
            @if (brand.status === 'loading') {
              <span class="size-10 shrink-0 rounded-lg bg-muted" aria-hidden="true"></span>
              <div class="min-w-0 flex-1">
                <p class="m-0 truncate text-lg font-bold">{{ brand.domain }}</p>
                <p class="m-0 flex items-center gap-2 text-base font-semibold text-muted-foreground">
                  <span class="size-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" aria-hidden="true"></span>
                  Finding logo and details…
                </p>
              </div>
            } @else {
              @if (brand.logoUrl) {
                <img [src]="brand.logoUrl" alt="" class="size-10 shrink-0 rounded-lg bg-muted object-cover" />
              } @else {
                <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-brand-agency-dark to-brand-agency text-sm font-black text-white" aria-hidden="true">{{ initial(brand.name) }}</span>
              }
              <div class="min-w-0 flex-1">
                <p class="m-0 truncate text-lg font-bold">{{ brand.name }}</p>
                <p class="m-0 truncate text-base font-semibold text-muted-foreground">{{ domainLabel(brand.domain) }}</p>
              </div>
            }
            <button type="button" class="btn-shout-fab-secondary shrink-0" [attr.aria-label]="removeLabel(brand)" (click)="remove(brand.id)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-4" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </li>
        }
      </ul>
    </div>
  }

  <div class="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background px-5 py-3 sm:static sm:z-auto sm:border-0 sm:bg-transparent sm:p-0">
    <button type="button" class="btn-shout btn-shout-md btn-shout-primary w-full justify-center" (click)="continueBrands()">
      {{ continueLabel() }}
    </button>
    <button type="button" class="mt-3 w-full text-center text-base font-bold text-primary" (click)="skip()">I'll add brands later</button>
  </div>
</div>
